import { Component, inject, signal } from '@angular/core';
import { AppIconService, ICON_DISABLED } from '@app/core/services/app-icon.service';
import { WindowLocationService } from '@app/core/services/window-location.service';
import { ButtonComponent } from '@app/shared/ui/button/button.component';
import { Capacitor } from '@capacitor/core';
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons';
import { TranslocoPipe } from '@jsverse/transloco';

@Component({
  imports: [TranslocoPipe, ButtonComponent],
  template: `
    <div class="new-app">
      <div class="new-app__hero">
        <div class="new-app__step-indicator">
          <span class="new-app__step" [class.new-app__step--active]="!understood()"></span>
          <span class="new-app__step" [class.new-app__step--active]="understood()"></span>
        </div>
        <img src="/assets/branded/img/logo.svg" class="new-app__logo" [alt]="'common.alt-image' | transloco" />
        <svg class="new-app__wave" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 80" preserveAspectRatio="none">
          <path d="M0,40 C360,80 1080,0 1440,40 L1440,80 L0,80 Z" fill="white" />
        </svg>
      </div>
      <div class="new-app__content">
        @if (!understood()) {
          <h2 class="new-app__title">{{ 'update.new-app-available-title' | transloco }}</h2>
          <p class="new-app__text">{{ 'update.new-app-available-description' | transloco }}</p>
        } @else {
          <h2 class="new-app__title">{{ 'update.new-app-install-title' | transloco }}</h2>
          <p class="new-app__text">{{ 'update.new-app-install-description' | transloco }}</p>
        }
        <div class="new-app__actions">
          @if (!understood()) {
            <c-button (action)="onUnderstood()">{{ 'update.action.understood' | transloco }}</c-button>
          } @else {
            <c-button [icon]="icon" iconPosition="end" (action)="doUpdate()">
              {{ 'update.action.install-new-app' | transloco }}
            </c-button>
          }
        </div>
      </div>
    </div>
  `,
  styles: [`
    .new-app {
      height: 100%;
      display: flex;
      flex-direction: column;
      background: white;
      color: black;
      overflow: hidden;
    }
    .new-app__hero {
      position: relative;
      background: var(--c-content-background);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 3rem 2rem 1rem;
      flex: 0 0 35%;
    }
    .new-app__step-indicator {
      position: absolute;
      top: 1.25rem;
      display: flex;
      gap: 0.4rem;
    }
    .new-app__step {
      width: 24px;
      height: 4px;
      border-radius: 2px;
      background: var(--ion-color-brand-contrast);
      opacity: 0.3;
      transition: opacity 0.3s ease;
    }
    .new-app__step--active {
      opacity: 1;
    }
    .new-app__logo {
      width: 60vw;
    }
    .new-app__wave {
      position: absolute;
      bottom: -1px;
      left: 0;
      width: 100%;
      height: 50px;
    }
    .new-app__content {
      flex: 1;
      display: flex;
      flex-direction: column;
      padding: 1.5rem 2rem 2.5rem;
      overflow: hidden;
      color: black;
    }
    .new-app__title {
      font-size: 1.4rem;
      font-weight: 700;
      margin: 0 0 0.75rem;
      line-height: 1.3;
    }
    .new-app__text {
      margin: 0;
      line-height: 1.7;
      font-size: 0.95rem;
    }
    .new-app__actions {
      margin-top: auto;
      padding-top: 1.5rem;
    }
  `]
})
export class AppUpdateNeededComponent {
    private readonly windowLocationService = inject(WindowLocationService);
    private readonly appIconService = inject(AppIconService);

    protected readonly understood = signal(false);

    readonly icon = faArrowRight;

    async onUnderstood(): Promise<void> {
        if (Capacitor.isNativePlatform()) {
            await this.appIconService.changeIcon(ICON_DISABLED);
        }
        this.understood.set(true);
    }

    doUpdate(): void {
        this.windowLocationService.openStore();
    }
}
